import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PCCardAction, ActionButton, Messages } from '../../components/Common';
import { faEdit, faCheckSquare } from '@fortawesome/free-solid-svg-icons';
import { Routes } from '../../Routes';
import { PCDataTable } from '../../components/DataTable/PCDataTable';
import { PermissionComponent } from '../../components/Common/PermissionComponent';
import { Permissions } from '../../Permissions';
import { ButtonGroup } from 'reactstrap';
import { ParameterGraphQLService } from '../../graphql/ParameterGraphQLService';
import { useParameterContext } from '../../components/Parameter';
import { PermissionButton } from '../../components/Forms';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';
import { ReleaseParameterModal } from '../../components/Common/Modals';
import { useRole } from '../../hooks';
import { ApolloError, useApolloClient } from '@apollo/client';
import { GraphQLError } from 'graphql';
import { TableError, PCDataTableResultPage, ColumnSortInfo } from '../../components/DataTable';
import { useHistory } from 'react-router-dom';
import { GraphQLUtil } from '../../graphql//GraphQLUtil';

/**
 * @author Jungwirth
 */
export const ParameterResultList: React.FC<{}> = (): JSX.Element => {
  const { filter: ctxFilter, messages, removeMessage } = useParameterContext();
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const [ error, setError] = useState<GraphQLError[]|undefined>(undefined);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [ result, setResult] = useState<PCDataTableResultPage<any>>({ data: [], page: { totalElements: 0, totalPages: 0} });
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const role = useRole();
  
  console.log('[ParameterResultList ] render', ctxFilter);

  const client = useApolloClient();
    
  // reset filter and pageIndex for new filter
  useEffect(() => {
    setError(undefined);
  },[ctxFilter]);


  const fetchData = useCallback(async ({ pageSize, pageIndex}, sortBy, filter) => {
    if(filter === undefined) {
      return;
    }

    const sort = GraphQLUtil.transformSortByList(sortBy);

    setLoading(true);
    try{
      const response = await client.query({ 
        query: ParameterGraphQLService.getParameterList(),
        variables: {
          number: (pageIndex+1),
          size: pageSize,
          filter: ParameterGraphQLService.getFilter(
            role,
            filter?.name, filter?.code, 
            filter?.dimension?.value ? JSON.parse(filter.dimension.value).code : undefined,
            filter?.material?.value ? JSON.parse(filter.material.value).code : undefined,
            filter?.non_material?.value ? JSON.parse(filter.non_material.value).code : undefined,
            filter?.unit?.value ? JSON.parse(filter.unit.value).code : undefined,
            filter?.sample_pretreatment?.value ? JSON.parse(filter.sample_pretreatment.value).code : undefined,
            filter?.method?.value ? JSON.parse(filter.method.value).code : undefined,
            filter?.matrix?.value ? JSON.parse(filter.matrix.value).code : undefined,
            filter?.other_criterion?.value ? JSON.parse(filter.other_criterion.value).code : undefined,
            filter?.status,
          ),
          sort: sort,
        },
        fetchPolicy: 'network-only',
      });
      if(response.errors) {
        setError([...response.errors]);
      }
      if(response.data) {
        const resultPage = response.data[Object.keys(response.data)[0]].page;
        history.push(
          { pathname: '/parameters',
            search: `?size=${resultPage.size}&page=${resultPage.number}`
          }
        );
        setError(undefined);
        setResult({ data: response.data[Object.keys(response.data)[0]].data, page: { ...resultPage }})
        setLoading(false);
      }
    } catch(e) {
      if(e instanceof ApolloError) {
        setError([...e.graphQLErrors]);
        setLoading(false);
      }
    } 

  },[client, role, history]);

  const selectRowFunc = useCallback((selected)=> {
    setSelectedRows(selected.map((e)=> e.original));
  },[]);


  const columns = useMemo(() => ParameterGraphQLService.getColumns(role, false), [role]);
  const defaultSort: ColumnSortInfo[] = useMemo(() => [{ id: 'lastModifiedDate', desc: true}],[]);
  
  return (
    <React.Fragment>
      <Messages messages={messages} removeMessage={removeMessage} />
      {ctxFilter && 
          
        <PCCardAction
          id="parameters_list"
          actions={() => (
            <React.Fragment>
              <PermissionComponent permission={[Permissions.ParameterCreate]}>
                <ActionButton id="button_new" icon={faEdit} to={Routes.ParameterNew} />
                <ActionButton id="button_new_with_search" icon={faEdit} to={`${Routes.ParameterNew}?searchparams=true`} />
              </PermissionComponent>
            </React.Fragment>
          )}
        >
          {error ?
            <TableError />
            :
            <React.Fragment>
              <PCDataTable
                columns={columns}
                data={result.data}
                filter={ctxFilter}
                loading={loading}
                fetchData={fetchData}
                pageInfo={result.page}
                defaultSort={defaultSort}
                selectedRows={selectRowFunc}
                serverMode
                sorting
                multisort
              />
              <ButtonGroup className="float-right" hidden={result.data.length === 0}>
                <PermissionButton 
                  permission={[Permissions.ParameterRelease]} color="release" className="mr-2"
                  disabled={selectedRows.length === 0}
                  onClick={() => {setReleaseOpen(true)}}
                >
                  <FontAwesomeIcon icon={faCheckSquare} />{' '}
                  <FormattedMessage id="button_release" />
                </PermissionButton>
              </ButtonGroup>
              <ReleaseParameterModal
                open={releaseOpen}
                setOpen={setReleaseOpen}
                items={selectedRows}
              />
            </React.Fragment>
          }
        </PCCardAction>
      }
    </React.Fragment>
  );
};
